import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type RefObject,
} from "react";

import { useMenu, type MenuGroup } from "./presence";

/**
 * The keyboard half of a menu, and the focus it hands back.
 *
 * A menu a mouse can drive but a keyboard cannot is half a menu, and it is
 * reliably the half that ships broken. Ported from the dropdown lab, whose
 * tests are the contract.
 */

/**
 * Hands focus back to the trigger when a menu closes.
 *
 * On Escape, and after a row is committed, focus is still inside the panel that
 * is about to disappear, so it has to go somewhere: it goes back to the button
 * that opened the menu, which is where the eye is.
 *
 * It does *not* go back when the press that closed the menu landed on another
 * control. That control now has focus because the browser gave it focus, and
 * pulling it back to the trigger would send the next keystroke to the wrong
 * place. A press on empty page leaves focus nowhere, and that does come back.
 */
export function useReturnFocus(
  open: boolean,
  boxRef: RefObject<HTMLElement | null>,
  triggerRef: RefObject<HTMLElement | null>,
  surfaceRef?: RefObject<HTMLElement | null>,
) {
  const wasOpen = useRef(open);
  /* The element to go back to, remembered while the menu is open rather than
     read from the ref at closing time. The phone body is portalled to <body>
     and its trigger lives inside a sheet that re-renders as the menu opens, so
     React can null the ref and re-set it in the same tick; reading it a
     moment later found null and focus was left on <body>, which drops a
     keyboard user back to the top of the page. Verified on the Discover city
     menu at 375px, where the tests did not catch it because jsdom re-renders
     do not churn refs the same way. */
  const returnTo = useRef<HTMLElement | null>(null);
  if (open && triggerRef.current) returnTo.current = triggerRef.current;

  useEffect(() => {
    const previously = wasOpen.current;
    wasOpen.current = open;
    if (!previously || open) return;
    if (typeof document === "undefined") return;

    const inside = (node: Element | null) =>
      !node ||
      node === document.body ||
      (boxRef.current?.contains(node) ?? false) ||
      (surfaceRef?.current?.contains(node) ?? false);

    if (!inside(document.activeElement)) return;

    /* The decision has to wait a beat. A press outside closes the menu on
       `pointerdown`, and the browser only moves focus afterwards: focusing the
       trigger straight away is undone a moment later by the press landing on
       whatever was underneath. So look again once the press has finished, and
       only take focus if nothing else has claimed it. */
    const timer = window.setTimeout(() => {
      if (!inside(document.activeElement)) return;
      const target = triggerRef.current ?? returnTo.current;
      // A trigger that has been unmounted cannot take focus, and calling
      // focus() on a detached node silently does nothing.
      if (target?.isConnected) target.focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [open, boxRef, triggerRef, surfaceRef]);
}

/**
 * The highlighted row, and the keys that move it.
 *
 * Resets to the chosen row each time the menu opens, clamps itself when a
 * search shortens the list, and keeps the highlight inside the scroll box
 * without scrolling the page.
 */
export function useRoving(open: boolean, count: number, selectedIndex = 0) {
  const [active, setActive] = useState(selectedIndex);
  const listRef = useRef<HTMLDivElement | null>(null);

  const wasOpen = useRef(open);
  useEffect(() => {
    if (open && !wasOpen.current) setActive(Math.max(0, selectedIndex));
    wasOpen.current = open;
  }, [open, selectedIndex]);

  useEffect(() => {
    setActive((i) => (count === 0 ? 0 : Math.min(i, count - 1)));
  }, [count]);

  // Keep the highlight in view by moving the list's own scrollTop. A
  // scrollIntoView would walk up and scroll the page as well.
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list || !open) return;
    const row = list.querySelector<HTMLElement>(`[data-row="${active}"]`);
    if (!row) return;
    const top = row.offsetTop;
    const bottom = top + row.offsetHeight;
    if (top < list.scrollTop) list.scrollTop = top;
    else if (bottom > list.scrollTop + list.clientHeight) {
      list.scrollTop = bottom - list.clientHeight;
    }
  }, [active, open]);

  return { active, setActive, listRef };
}

/**
 * One keydown handler for a menu box, wired the same way everywhere.
 *
 * Escape is missing on purpose: `useMenu` already takes it on the document in
 * the capture phase, so the key closes the menu and never reaches whatever is
 * behind it.
 */
export function menuKeys({
  open,
  setOpen,
  count,
  active,
  setActive,
  commit,
  onSpace,
}: {
  open: boolean;
  setOpen: (next: boolean) => void;
  count: number;
  active: number;
  setActive: (next: number) => void;
  commit: (index: number) => void;
  /** Multi-select uses Space to toggle without closing. */
  onSpace?: (index: number) => void;
}) {
  return (event: ReactKeyboardEvent) => {
    if (!open) {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (event.key === "Tab") {
      setOpen(false);
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive(count === 0 ? 0 : Math.min(count - 1, active + 1));
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive(Math.max(0, active - 1));
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      setActive(0);
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      setActive(Math.max(0, count - 1));
      return;
    }
    if (event.key === " " && onSpace) {
      event.preventDefault();
      if (count > 0) onSpace(active);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      if (count > 0) commit(active);
    }
  };
}

/**
 * Everything a menu needs to be a working control: the group membership, the
 * trigger ref, and focus return.
 */
export function useDropdown(
  name: string,
  options?: {
    group?: MenuGroup;
    /** The portalled bottom sheet, which is outside the box. */
    surfaceRef?: RefObject<HTMLElement | null>;
    /** Opens itself on mount. Used by the design lab's frozen examples. */
    defaultOpen?: boolean;
    openNonce?: number;
  },
) {
  const menu = useMenu<HTMLDivElement>(options?.group, name, {
    surfaceRef: options?.surfaceRef,
  });
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  useReturnFocus(menu.open, menu.ref, triggerRef, options?.surfaceRef);

  const { setOpen } = menu;
  const wantOpen = options?.defaultOpen ?? false;
  const nonce = options?.openNonce ?? 0;
  useEffect(() => {
    if (wantOpen) setOpen(true);
  }, [wantOpen, nonce, setOpen]);

  return { ...menu, triggerRef };
}

/**
 * Whether the screen is narrow enough for the sheet body.
 *
 * `false` on the server and on the first client frame, so the markup matches
 * across hydration. Nothing is rendered either way until a menu is opened, and
 * that can only happen after a press.
 */
export function useNarrowViewport(maxWidth: number): boolean {
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const apply = () => setNarrow(mq.matches);
    apply();
    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", apply);
      return () => mq.removeEventListener("change", apply);
    }
    return undefined;
  }, [maxWidth]);
  return narrow;
}

/**
 * The document's writing direction, so a menu grows out of the corner it hangs
 * from in Arabic as well as in English. Read after mount, and watched, because
 * the locale switcher flips it in place.
 */
export function useDocumentDir(): "ltr" | "rtl" {
  const [dir, setDir] = useState<"ltr" | "rtl">("ltr");
  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    const read = () => setDir(root.dir === "rtl" ? "rtl" : "ltr");
    read();
    if (typeof MutationObserver === "undefined") return;
    const observer = new MutationObserver(read);
    observer.observe(root, { attributes: true, attributeFilter: ["dir"] });
    return () => observer.disconnect();
  }, []);
  return dir;
}
